import { Archive, ArrowUpRight, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useOrganization } from "@/client/backend/organization";
import {
	formatArchiveSize,
	shortInstanceId,
	splitXcodeVersion,
	useOrganizationXcodeSnapshots,
	useOrganizationXcodeSnapshot,
	type XcodeSnapshot,
} from "@/client/backend/xcodeSnapshots";
import { CopyButton, CopyableCommand } from "@/components/copy";
import { SnapshotDeleteButton } from "@/components/snapshot-delete-button";
import { SnapshotTabs } from "@/components/snapshot-tabs";
import { AppShell, EmptyState, PageHeader } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatRelativeTime } from "@/lib/format";

import { IOSSnapshotsPage } from "./ios-snapshots";

const docsUrl = "https://docs.limrun.com/ios/snapshots";

function Paths({ paths }: { paths?: string[] }) {
	if (!paths || paths.length === 0) return <span className="text-muted-foreground">Whole workspace</span>;
	return (
		<span className="flex flex-wrap gap-1">
			{paths.map((path) => (
				<Badge key={path} variant="outline" className="font-mono text-xs">
					{path}
				</Badge>
			))}
		</span>
	);
}

function XcodeVersion({ versionKey }: { versionKey: string }) {
	const { version, build } = splitXcodeVersion(versionKey);
	return (
		<span>
			{version}
			{build && <span className="ml-1 text-xs text-muted-foreground">{build}</span>}
		</span>
	);
}

function Published({ at }: { at: string }) {
	const date = new Date(at);
	return (
		<span className="flex flex-col">
			<span>{date.toLocaleString()}</span>
			<span className="text-xs text-muted-foreground">{formatRelativeTime(at)}</span>
		</span>
	);
}

function SummaryTile({ label, value, detail }: { label: string; value: string; detail?: string }) {
	return (
		<Card className="min-w-0 flex-1 basis-44">
			<CardContent className="p-4">
				<p className="text-xs text-muted-foreground">{label}</p>
				<p className="mt-1 text-2xl font-semibold tracking-tight">{value}</p>
				{detail && <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>}
			</CardContent>
		</Card>
	);
}

function SnapshotList({ snapshots, organizationId, refresh }: { snapshots: XcodeSnapshot[]; organizationId?: string; refresh: () => void }) {
	const [prefix, setPrefix] = useState("");
	const visible = useMemo(
		() => (prefix ? snapshots.filter((snapshot) => snapshot.key.startsWith(prefix)) : snapshots),
		[snapshots, prefix],
	);
	const workspaces = useMemo(() => {
		const byDirectory = new Map<string, number>();
		for (const snapshot of snapshots) byDirectory.set(snapshot.directoryKey, (byDirectory.get(snapshot.directoryKey) ?? 0) + 1);
		return byDirectory;
	}, [snapshots]);
	const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
	const publishedThisWeek = snapshots.filter((snapshot) => Date.parse(snapshot.publishedAt) >= weekAgo).length;
	const totalBytes = snapshots.reduce((sum, snapshot) => sum + snapshot.archiveSizeBytes, 0);
	const largest = snapshots.reduce<XcodeSnapshot | undefined>(
		(best, snapshot) => (!best || snapshot.archiveSizeBytes > best.archiveSizeBytes ? snapshot : best),
		undefined,
	);
	const projects = new Set(snapshots.map((snapshot) => snapshot.rootName)).size;
	return (
		<div className="flex flex-col gap-6">
			<section aria-label="Summary" className="flex flex-wrap gap-3">
				<SummaryTile label="Snapshots" value={String(snapshots.length)} detail={`across ${projects} ${projects === 1 ? "project" : "projects"}`} />
				<SummaryTile label="Stored" value={formatArchiveSize(totalBytes)} detail="compressed archives" />
				<SummaryTile
					label="Published this week"
					value={String(publishedThisWeek)}
					detail={snapshots[0] ? `latest ${formatRelativeTime(snapshots[0].publishedAt)}` : undefined}
				/>
				<SummaryTile label="Largest" value={largest ? formatArchiveSize(largest.archiveSizeBytes) : "—"} detail={largest?.key} />
			</section>
			<section aria-label="Snapshots" className="flex flex-col gap-3">
				<div className="flex flex-wrap items-center gap-2">
					<Input
						value={prefix}
						onChange={(event) => setPrefix(event.target.value.trim())}
						placeholder="Filter by key prefix…"
						aria-label="Filter snapshots by key prefix"
						className="h-8 w-full border-border bg-transparent shadow-none sm:ml-auto sm:w-60"
					/>
				</div>
				<div className="overflow-x-auto">
					<Table aria-label="Snapshots">
						<TableHeader>
							<TableRow>
								<TableHead className="w-[32%]">Key</TableHead>
								<TableHead className="hidden md:table-cell">Project</TableHead>
								<TableHead className="hidden md:table-cell">Paths</TableHead>
								<TableHead className="hidden md:table-cell">Xcode</TableHead>
								<TableHead>Size</TableHead>
								<TableHead>Published</TableHead>
								<TableHead className="w-12">
									<span className="sr-only">Actions</span>
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{visible.map((snapshot) => {
								const shared = (workspaces.get(snapshot.directoryKey) ?? 1) - 1;
								return (
									<TableRow key={snapshot.key}>
										<TableCell className="max-w-96">
											<Link to={`/snapshots/${encodeURIComponent(snapshot.key)}`} className="block truncate font-semibold hover:underline" title={snapshot.key}>
												{snapshot.key}
											</Link>
											<span className="block truncate text-xs text-muted-foreground">
												{snapshot.sourceInstanceId && (
													<>
														published by <span className="font-mono">{shortInstanceId(snapshot.sourceInstanceId)}</span>
													</>
												)}
												{snapshot.sourceInstanceId && shared > 0 && " · "}
												{shared > 0 && `shares a workspace with ${shared} other ${shared === 1 ? "key" : "keys"}`}
											</span>
										</TableCell>
										<TableCell className="hidden font-mono text-xs md:table-cell">{snapshot.rootName}</TableCell>
										<TableCell className="hidden md:table-cell">
											<Paths paths={snapshot.paths} />
										</TableCell>
										<TableCell className="hidden md:table-cell">
											<XcodeVersion versionKey={snapshot.xcodeVersionKey} />
										</TableCell>
										<TableCell>{formatArchiveSize(snapshot.archiveSizeBytes)}</TableCell>
										<TableCell>
											<Published at={snapshot.publishedAt} />
										</TableCell>
										<TableCell>
											<div className="flex items-center">
												<CopyButton value={snapshot.key} label={`Copy key ${snapshot.key}`} />
												{organizationId && (
													<SnapshotDeleteButton kind="xcode" organizationId={organizationId} snapshotKey={snapshot.key} onDeleted={refresh} />
												)}
											</div>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</div>
				<p className="text-xs text-muted-foreground">
					{prefix ? `Showing ${visible.length} of ${snapshots.length} snapshots.` : `Showing ${snapshots.length} snapshots.`} Archives expire a fixed time after their last publication.
				</p>
			</section>
		</div>
	);
}

function SnapshotDetail({ snapshot, siblings }: { snapshot: XcodeSnapshot; siblings: XcodeSnapshot[] }) {
	const { version, build } = splitXcodeVersion(snapshot.xcodeVersionKey);
	const shared = siblings.filter((other) => other.key !== snapshot.key && other.directoryKey === snapshot.directoryKey);
	const sdkSnippet = `const instance = await lim.xcodeInstances.create({\n  wait: true,\n  spec: { snapshot: { restoreKeys: ['${snapshot.key}'] } },\n});`;
	return (
		<div className="flex max-w-4xl flex-col gap-6">
			<Card variant="section">
				<CardHeader>
					<CardTitle>Archive</CardTitle>
					<CardDescription>What the next restore materializes. Publishing under this key replaces it in place.</CardDescription>
				</CardHeader>
				<CardContent>
					<dl className="grid grid-cols-[10rem_minmax(0,1fr)] gap-x-4 gap-y-3 text-sm">
						<dt className="text-xs text-muted-foreground">Key</dt>
						<dd className="flex min-w-0 items-center gap-1 break-all font-semibold">
							{snapshot.key}
							<CopyButton value={snapshot.key} label="Copy key" />
						</dd>
						<dt className="text-xs text-muted-foreground">Project root</dt>
						<dd className="min-w-0">
							<span className="font-mono text-xs">{snapshot.rootName}</span>
							<span className="ml-2 text-xs text-muted-foreground">A workspace synced under another name cannot reuse this state.</span>
						</dd>
						<dt className="text-xs text-muted-foreground">Paths</dt>
						<dd className="min-w-0">
							<Paths paths={snapshot.paths} />
							<span className="ml-2 text-xs text-muted-foreground">Restores must use the same path set.</span>
						</dd>
						<dt className="text-xs text-muted-foreground">Xcode</dt>
						<dd className="min-w-0">
							{version}
							{build && <span className="ml-1 font-mono text-xs">{build}</span>}
							<span className="ml-2 text-xs text-muted-foreground">Restored into another Xcode version it starts cold.</span>
						</dd>
						<dt className="text-xs text-muted-foreground">Size</dt>
						<dd className="min-w-0">
							{formatArchiveSize(snapshot.archiveSizeBytes)} compressed
							<span className="ml-2 text-xs text-muted-foreground">
								{snapshot.archiveSizeBytes.toLocaleString()} bytes, {snapshot.archiveFormat}
							</span>
						</dd>
						<dt className="text-xs text-muted-foreground">Checksum</dt>
						<dd className="flex min-w-0 items-center gap-1 break-all font-mono text-xs">
							sha256 {snapshot.sha256}
							<CopyButton value={snapshot.sha256} label="Copy checksum" />
						</dd>
						<dt className="text-xs text-muted-foreground">Published</dt>
						<dd className="min-w-0">
							{new Date(snapshot.publishedAt).toLocaleString()}
							<span className="ml-1 text-xs text-muted-foreground">({formatRelativeTime(snapshot.publishedAt)})</span>
							{snapshot.sourceInstanceId && (
								<>
									<span className="ml-2 text-xs text-muted-foreground">by</span>{" "}
									<Link to={`/builds/${encodeURIComponent(snapshot.sourceInstanceId)}`} className="font-mono text-xs hover:underline">
										{snapshot.sourceInstanceId}
									</Link>
								</>
							)}
						</dd>
						<dt className="text-xs text-muted-foreground">Expires</dt>
						<dd className="text-xs text-muted-foreground">A fixed time after the last publication; each publish resets it.</dd>
						<dt className="text-xs text-muted-foreground">Shared workspace</dt>
						<dd className="min-w-0 text-xs">
							{shared.length === 0 ? (
								<span className="text-muted-foreground">No other key restores into this workspace.</span>
							) : (
								<>
									<span className="text-muted-foreground">{shared.length === 1 ? "1 other key keeps this layout: " : `${shared.length} other keys keep this layout: `}</span>
									{shared.map((other, index) => (
										<span key={other.key}>
											{index > 0 && ", "}
											<Link to={`/snapshots/${encodeURIComponent(other.key)}`} className="hover:underline">
												{other.key}
											</Link>
										</span>
									))}
								</>
							)}
						</dd>
					</dl>
				</CardContent>
			</Card>
			<Card variant="section">
				<CardHeader>
					<CardTitle>Use this snapshot</CardTitle>
					<CardDescription>
						Restore without replacing it. Pass <span className="font-mono">--snapshot-key</span> only on the build that should publish the next version.
					</CardDescription>
				</CardHeader>
				<CardContent className="flex flex-col gap-3">
					<CopyableCommand command={`lim xcode create --snapshot-restore-keys ${snapshot.key}`} label="Copy the create command" />
					<pre className="overflow-x-auto rounded-lg border bg-muted/40 p-3 font-mono text-xs">{sdkSnippet}</pre>
				</CardContent>
			</Card>
		</div>
	);
}

function XcodeSnapshotsPage() {
	const { key } = useParams<{ key?: string }>();
	const navigate = useNavigate();
	const [organization] = useOrganization();
	const organizationTid = organization !== "loading" ? organization.tid : undefined;
	const { data, error, mutate, isValidating } = useOrganizationXcodeSnapshots(organizationTid);
	const detail = useOrganizationXcodeSnapshot(organizationTid, key);
	const snapshots = data?.data?.snapshots;
	const loaded = key ? detail.data !== undefined : data !== undefined;
	const loadError = key ? detail.error : error;
	const selected = key ? detail.data : undefined;
	const title = key ?? "Snapshots";

	return (
		<AppShell title={title}>
			<div className="flex flex-col gap-6">
				{key ? (
					<nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
						<Link to="/snapshots" className="hover:underline">
							Snapshots
						</Link>
						<span aria-hidden="true" className="mx-1">
							/
						</span>
						<span className="break-all">{key}</span>
					</nav>
				) : null}
				<PageHeader
					title={title}
					description={
						key
							? selected
								? `Published ${formatRelativeTime(selected.publishedAt)} · ${formatArchiveSize(selected.archiveSizeBytes)} · Xcode ${splitXcodeVersion(selected.xcodeVersionKey).version} · project ${selected.rootName}`
								: undefined
							: "Saved Xcode workspaces your organization restores from. Each key holds the latest archive published under it."
					}
					actions={
						<div className="flex flex-wrap gap-2">
							<Button variant="outline" disabled={isValidating || detail.isValidating} onClick={() => {
								void mutate();
								if (key) void detail.mutate();
							}}>
								<RefreshCw className="h-4 w-4" />Refresh
							</Button>
							{key && selected && organizationTid && (
								<SnapshotDeleteButton kind="xcode" organizationId={organizationTid} snapshotKey={key} onDeleted={() => navigate("/snapshots")} />
							)}
							<Button variant="outline" asChild>
								<a href={docsUrl} target="_blank" rel="noreferrer">
									Docs
									<ArrowUpRight className="h-4 w-4" />
								</a>
							</Button>
						</div>
					}
				/>
				<SnapshotTabs />
				{loadError && <p role="alert" className="text-sm text-danger">Failed to load snapshots: {loadError.message}</p>}
				{loadError && !loaded ? null : !loaded ? (
					<p className="text-sm text-muted-foreground" role="status">
						Loading snapshots…
					</p>
				) : key ? (
					selected ? (
						<SnapshotDetail snapshot={selected} siblings={snapshots ?? []} />
					) : (
						<EmptyState icon={<Archive />} title="No snapshot under this key" description="It was never published, or it was deleted." />
					)
				) : !snapshots?.length ? (
					<EmptyState
						icon={<Archive />}
						title="No snapshots yet"
						description="Create an Xcode instance with a snapshot key, build, then delete the instance. Its workspace is saved under that key and the next instance restores it before building."
					>
						<CopyableCommand command="lim xcode create --snapshot-key myapp-main --quiet" label="Copy the create command" />
					</EmptyState>
				) : (
					<SnapshotList key={organizationTid} snapshots={snapshots} organizationId={organizationTid} refresh={() => void mutate()} />
				)}
			</div>
		</AppShell>
	);
}

export function SnapshotsPage() {
	const [params] = useSearchParams();
	return params.get("type") === "ios" ? <IOSSnapshotsPage /> : <XcodeSnapshotsPage />;
}
